import { Menu } from "lucide-react";
import type { ReactNode, RefObject } from "react";
import { cn } from "../../lib/utils";
import { LogoIcon } from "../ui/logo-icon";
import { ReleaseBadge } from "../ui/release-badge";

/**
 * The 48px top bar (plan §1.2 / `.context/mockups/shell.html`):
 *
 *     ▣ INTERIORING  v2.31              ⌕        ⚲ 3     ◑     SR ▾
 *
 * A hairline under it. No shadow, no second row — the shell's whole vertical
 * budget above the content is three rem.
 *
 * The mockup's global search is NOT here: there is no cross-entity search in
 * the pro portal to wire it to (the only search surface is the admin page at
 * `/admin/search`). A box that returns nothing is worse than no box, so the
 * slot is left to the `children` the layouts actually have.
 */
export type TopBarProps = {
	/** Opens the rail drawer. Omit on surfaces with no drawer. */
	onOpenNav?: () => void;
	navOpen?: boolean;
	menuButtonRef?: RefObject<HTMLButtonElement | null>;
	/**
	 * Where the hamburger shows. Defaults to "below the static rail". The pro
	 * shell narrows it to tablets, because phones navigate from the bottom.
	 */
	menuClassName?: string;
	/** A `.label` beside the brand: "Admin". */
	label?: string;
	/** The right-hand controls: bell, theme, whatever the surface has. */
	children?: ReactNode;
};

export function TopBar({
	onOpenNav,
	navOpen,
	menuButtonRef,
	menuClassName = "lg:hidden",
	label,
	children,
}: TopBarProps) {
	return (
		<div className="flex h-12 items-center gap-2 border-b border-border-default bg-background-elevated px-3 sm:px-4">
			{onOpenNav && (
				<button
					ref={menuButtonRef}
					type="button"
					onClick={onOpenNav}
					aria-label="Open navigation"
					aria-expanded={navOpen ?? false}
					className={cn(
						"-ml-1 flex size-8 items-center justify-center text-foreground-muted transition-colors hover:text-foreground-default pointer-coarse:size-11",
						menuClassName,
					)}
				>
					<Menu className="size-5" aria-hidden="true" />
				</button>
			)}

			<span className="flex min-w-0 items-center gap-1.5">
				<LogoIcon size={24} className="flex-none text-foreground-default" />
				<span className="truncate text-sm font-semibold text-foreground-default">
					Interioring
				</span>
				{label && (
					<span className="text-xs font-semibold uppercase tracking-wide text-foreground-muted">
						{label}
					</span>
				)}
				<ReleaseBadge />
			</span>

			<span className="ml-auto flex items-center gap-1">{children}</span>
		</div>
	);
}
